import {useMemo, useState} from "react";
import type {LucideIcon} from "lucide-react";
import {cn} from "@/lib/utils";

export type AdminModalKpiItem = {
  id: string;
  label: string;
  value: string | number;
  hint?: string;
  icon?: LucideIcon;
  onClick?: () => void;
};

type Props = {
  items: AdminModalKpiItem[];
  className?: string;
};

/** Mini KPI strip for modal headers / workspace dashboards. */
export function AdminModalKpis({items, className}: Props) {
  return (
    <div className={cn("admin-modal-kpis", className)}>
      {items.map((item) => {
        const Icon = item.icon;
        const Comp = item.onClick ? "button" : "div";
        return (
          <Comp
            key={item.id}
            type={item.onClick ? "button" : undefined}
            className={cn("admin-modal-kpi", item.onClick && "admin-modal-kpi--clickable")}
            onClick={item.onClick}
          >
            {Icon ? (
              <span className="admin-modal-kpi__icon" aria-hidden>
                <Icon className="h-3.5 w-3.5" />
              </span>
            ) : null}
            <span className="admin-modal-kpi__copy">
              <span className="admin-modal-kpi__label">{item.label}</span>
              <span className="admin-modal-kpi__value">{item.value}</span>
              {item.hint ? <span className="admin-modal-kpi__hint">{item.hint}</span> : null}
            </span>
          </Comp>
        );
      })}
    </div>
  );
}

export function useClientInitials(name: string): string {
  return useMemo(() => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if(!parts.length) return "?";
    if(parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }, [name]);
}

export function AdminEntityAvatar({name, className}: {name: string; className?: string}) {
  const [broken, setBroken] = useState(false);
  const initials = useClientInitials(name);
  void broken;
  void setBroken;
  return (
    <div className={cn("admin-entity-avatar", className)} aria-hidden>
      <span>{initials}</span>
    </div>
  );
}
